import * as React from 'react'
import {
  ContextMenuBase as ContextMenu,
  ContextMenuTriggerBase as ContextMenuTrigger,
  ContextMenuContentBase as ContextMenuContent,
  ContextMenuItemBase as ContextMenuItem,
  ContextMenuCheckboxItemBase as ContextMenuCheckboxItem,
  ContextMenuRadioItemBase as ContextMenuRadioItem,
  ContextMenuLabelBase as ContextMenuLabel,
  ContextMenuSeparatorBase as ContextMenuSeparator,
  ContextMenuShortcutBase as ContextMenuShortcut,
  ContextMenuGroupBase as ContextMenuGroup,
  ContextMenuSubBase as ContextMenuSub,
  ContextMenuSubTriggerBase as ContextMenuSubTrigger,
  ContextMenuSubContentBase as ContextMenuSubContent,
  ContextMenuRadioGroupBase as ContextMenuRadioGroup,
} from '@/components/ui/context-menu'
import { cn } from '@/lib/utils'

/**
 * Figma (node 430:18718 — Context Menu):
 *
 * Content (node 249:5013):
 *   bg-popover, border border-border, rounded-lg (10px), shadow-md, p-1 (4px)
 *
 * Item (node 419:4521):
 *   rounded-md (8px), px-1.5 (6px), py-1 (4px), gap-1.5 (6px), text-sm (14px), max-h-7 (28px)
 *   Variants: Default, Radio, Destructive
 *   States: Default, Hover (bg-accent), Disabled (opacity-50)
 *   Destructive: text-destructive, hover bg-destructive/10
 *
 * Label (node 419:4517):
 *   text-xs (12px), font-medium, text-muted-foreground, px-1.5 py-1
 *
 * Separator (node 249:5008):
 *   h-px bg-border, -mx-1 my-1
 *
 * SubTrigger (node 419:4534):
 *   Same as Item + ChevronRight icon, data-[state=open]:bg-accent
 */

/* ─── Root ─── */
/* Thin wrapper to avoid TDZ — circular dep means ContextMenu is uninitialized at module scope */
const IntuiContextMenu = (props: React.ComponentProps<typeof ContextMenu>) => <ContextMenu {...props} />

/* ─── Trigger ─── */
const IntuiContextMenuTrigger = React.forwardRef<
  React.ElementRef<typeof ContextMenuTrigger>,
  React.ComponentPropsWithoutRef<typeof ContextMenuTrigger>
>((props, ref) => <ContextMenuTrigger ref={ref} {...props} />)
IntuiContextMenuTrigger.displayName = 'IntuiContextMenuTrigger'

/* ─── Content ─── */
const IntuiContextMenuContent = React.forwardRef<
  React.ElementRef<typeof ContextMenuContent>,
  React.ComponentPropsWithoutRef<typeof ContextMenuContent>
>(({ className, ...props }, ref) => (
  <ContextMenuContent
    ref={ref}
    className={cn('rounded-lg', className)}
    {...props}
  />
))
IntuiContextMenuContent.displayName = 'IntuiContextMenuContent'

/* ─── Item ─── */
export interface IntuiContextMenuItemProps
  extends React.ComponentPropsWithoutRef<typeof ContextMenuItem> {
  /** Render item with destructive (red) styling */
  variant?: 'default' | 'destructive'
  /** Optional icon rendered before the label */
  icon?: React.ReactNode
  /** Keyboard shortcut displayed on the right */
  shortcut?: string
}

const IntuiContextMenuItem = React.forwardRef<
  React.ElementRef<typeof ContextMenuItem>,
  IntuiContextMenuItemProps
>(({ className, variant = 'default', icon, shortcut, children, ...props }, ref) => (
  <ContextMenuItem
    ref={ref}
    className={cn(
      'rounded-md px-1.5 py-1 gap-1.5 text-sm max-h-7',
      variant === 'destructive' &&
        'text-destructive focus:text-destructive focus:bg-destructive/10',
      className,
    )}
    {...props}
  >
    {icon && (
      <span className="flex items-center justify-center [&>svg]:size-4 [&>svg]:shrink-0">
        {icon}
      </span>
    )}
    {children}
    {shortcut && <ContextMenuShortcut>{shortcut}</ContextMenuShortcut>}
  </ContextMenuItem>
))
IntuiContextMenuItem.displayName = 'IntuiContextMenuItem'

/* ─── Label ─── */
const IntuiContextMenuLabel = React.forwardRef<
  React.ElementRef<typeof ContextMenuLabel>,
  React.ComponentPropsWithoutRef<typeof ContextMenuLabel>
>(({ className, ...props }, ref) => (
  <ContextMenuLabel
    ref={ref}
    className={cn('px-1.5 py-1 text-xs font-medium text-muted-foreground', className)}
    {...props}
  />
))
IntuiContextMenuLabel.displayName = 'IntuiContextMenuLabel'

/* ─── Separator ─── */
const IntuiContextMenuSeparator = React.forwardRef<
  React.ElementRef<typeof ContextMenuSeparator>,
  React.ComponentPropsWithoutRef<typeof ContextMenuSeparator>
>(({ className, ...props }, ref) => (
  <ContextMenuSeparator ref={ref} className={cn('bg-border', className)} {...props} />
))
IntuiContextMenuSeparator.displayName = 'IntuiContextMenuSeparator'

/* ─── Sub ─── */
const IntuiContextMenuSub = (props: React.ComponentProps<typeof ContextMenuSub>) => <ContextMenuSub {...props} />

/* ─── SubTrigger ─── */
const IntuiContextMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof ContextMenuSubTrigger>,
  React.ComponentPropsWithoutRef<typeof ContextMenuSubTrigger>
>(({ className, ...props }, ref) => (
  <ContextMenuSubTrigger
    ref={ref}
    className={cn('rounded-md px-1.5 py-1 gap-1.5 text-sm', className)}
    {...props}
  />
))
IntuiContextMenuSubTrigger.displayName = 'IntuiContextMenuSubTrigger'

/* ─── SubContent ─── */
const IntuiContextMenuSubContent = React.forwardRef<
  React.ElementRef<typeof ContextMenuSubContent>,
  React.ComponentPropsWithoutRef<typeof ContextMenuSubContent>
>(({ className, ...props }, ref) => (
  <ContextMenuSubContent
    ref={ref}
    className={cn('rounded-lg', className)}
    {...props}
  />
))
IntuiContextMenuSubContent.displayName = 'IntuiContextMenuSubContent'

/* ─── CheckboxItem ─── */
const IntuiContextMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof ContextMenuCheckboxItem>,
  React.ComponentPropsWithoutRef<typeof ContextMenuCheckboxItem>
>(({ className, ...props }, ref) => (
  <ContextMenuCheckboxItem
    ref={ref}
    className={cn('rounded-md', className)}
    {...props}
  />
))
IntuiContextMenuCheckboxItem.displayName = 'IntuiContextMenuCheckboxItem'

/* ─── RadioGroup ─── */
const IntuiContextMenuRadioGroup = (props: React.ComponentProps<typeof ContextMenuRadioGroup>) => <ContextMenuRadioGroup {...props} />

/* ─── RadioItem ─── */
const IntuiContextMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof ContextMenuRadioItem>,
  React.ComponentPropsWithoutRef<typeof ContextMenuRadioItem>
>(({ className, ...props }, ref) => (
  <ContextMenuRadioItem
    ref={ref}
    className={cn('rounded-md', className)}
    {...props}
  />
))
IntuiContextMenuRadioItem.displayName = 'IntuiContextMenuRadioItem'

/* ─── Group ─── */
const IntuiContextMenuGroup = (props: React.ComponentProps<typeof ContextMenuGroup>) => <ContextMenuGroup {...props} />

/* ─── Shortcut (convenience re-export) ─── */
const IntuiContextMenuShortcut = (props: React.ComponentProps<typeof ContextMenuShortcut>) => <ContextMenuShortcut {...props} />

export {
  IntuiContextMenu,
  IntuiContextMenuTrigger,
  IntuiContextMenuContent,
  IntuiContextMenuItem,
  IntuiContextMenuLabel,
  IntuiContextMenuSeparator,
  IntuiContextMenuSub,
  IntuiContextMenuSubTrigger,
  IntuiContextMenuSubContent,
  IntuiContextMenuCheckboxItem,
  IntuiContextMenuRadioGroup,
  IntuiContextMenuRadioItem,
  IntuiContextMenuGroup,
  IntuiContextMenuShortcut,
}
